// Copyright (C) 2022 by Posit Software, PBC.

import {
  deleteContentThumbnail,
  getContentThumbnail,
  setContentThumbnail,
} from "@/api/app";
import { getContent, updateContent } from "@/api/content";
import AppModes from "@/api/dto/appMode";
import AppRoles from "@/api/dto/appRole";
import { updateGitRepository } from "@/api/git";
import {
  drainPaging,
  getContentVisits,
  getShinyUsage,
} from "@/api/instrumentation";
import { getUser } from "@/api/users";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import minMax from "dayjs/plugin/minMax";
import isEmpty from "lodash/isEmpty";
import { FETCH_GIT_INFO, UPDATE_APP_METADATA } from "./contentView";

export const INFO_SETTINGS_INIT = "INFO_SETTINGS_INIT";
export const INFO_SETTINGS_RECEIVED = "INFO_SETTINGS_RECEIVED";
export const INFO_SETTINGS_ERROR = "INFO_SETTINGS_ERROR";
export const FETCH_USAGE = "FETCH_USAGE";
export const USAGE_DATA_RECEIVED = "USAGE_DATA_RECEIVED";
export const SESSION_TIME_RECEIVED = "SESSION_TIME_RECEIVED";
export const USAGE_DATA_ERROR = "USAGE_DATA_ERROR";
export const RESOLVE_CHART_DATA = "RESOLVE_CHART_DATA";
export const CHART_DATA_RECEIVED = "CHART_DATA_RECEIVED";
export const RESET_CHART_DATA = "RESET_CHART_DATA";
export const THUMBNAIL_URL_RECEIVED = "THUMBNAIL_URL_RECEIVED";
export const THUMBNAIL_RECEIVED = "THUMBNAIL_RECEIVED";
export const FETCH_THUMBNAIL = "FETCH_THUMBNAIL";
export const INFO_SETTINGS_DISCARD = "INFO_SETTINGS_DISCARD";
export const SAVE_INFO_SETTINGS = "SAVE_INFO_SETTINGS";

// Constants from go code.
// https://github.com/posit-dev/connect/blob/e16f085ca8ca0e658feadd24cc0b054d34adf3e2/src/connect/api/instrumentation/types.go#L18
const MAX_CONTENT_USAGE_API_LIMIT = 500;
const CONTENT_USAGE_API_VERSION = "1";
const CONTENT_USAGE_API_TOTAL_LIMIT = 10000;

// We use this format to represent day labels.
const DAY_FORMAT = "ll";

dayjs.extend(duration);
dayjs.extend(minMax);

/**
 * Enum covering the different API Types for the Content Usage APIs
 *
 * @typedef {number} ContentUsageAPIType
 * @readonly
 * @enum {ContentUsageAPIType}
 */
export const ContentUsageAPIType = {
  General: "general",
  Shiny: "shiny",
};

/**
 * Utility method to determine what API should be called to retrieve
 * usage data for a specific content type.
 *
 * Note: Not all content types are supported by the APIs.
 *
 * @param {AppMode} appMode - Application Type
 *
 * @returns {ContentUsageAPIType} - The Corresponding API Type which
 *          indicates the API which should be used to retrieve usage data
 *          from the Connect server.
 */
export function getContentUsageAPIType(appMode) {
  switch (appMode) {
    case AppModes.Shiny:
    case AppModes.ShinyRmd:
    case AppModes.ShinyQuarto:
    case AppModes.PythonShiny:
      return ContentUsageAPIType.Shiny;
    default:
      return ContentUsageAPIType.General;
  }
}

function userObjectToOwnerProperties(user) {
  if (user) {
    return {
      ownerEmail: user.email,
      ownerFirstName: user.firstName,
      ownerLastName: user.lastName,
      ownerUsername: user.username,
      ownerLocked: user.locked,
    };
  }
  return {
    ownerEmail: "",
    ownerFirstName: "",
    ownerLastName: "[Undisclosed owner]",
    ownerUsername: "",
    ownerLocked: false,
  };
}

const staticRmdAppDescriptor = (isSite, parameterized) => {
  if (isSite) {
    return "R Markdown site";
  } else if (parameterized) {
    return "Parameterized R Markdown document";
  }
  return "R Markdown document";
};

const staticQuartoAppDescriptor = (isSite) => {
  if (isSite) {
    return "Quarto project";
  }
  return "Quarto document";
};

const staticAppDescriptor = (isSite, isPlot, isPin) => {
  if (isSite) {
    return "Static site";
  } else if (isPlot) {
    return "Static plot";
  } else if (isPin) {
    return "Pin";
  }
  return "Static document";
};

const isSite = (app) =>
  app.contentCategory === "site" || app.contentCategory === "book";

const isPlot = (app) => app.contentCategory === "plot";

const isPin = (app) => app.contentCategory === "pin";

function getAppTypeDescription(app) {
  switch (app.appMode) {
    case AppModes.Unknown:
      return "Incomplete content";

    case AppModes.Shiny:
      return "Shiny application";

    case AppModes.ShinyRmd:
      return "Shiny R Markdown document";

    case AppModes.ShinyQuarto:
      return "Shiny Quarto document";

    case AppModes.StaticRmd:
      return staticRmdAppDescriptor(isSite(app), app.parameterized);

    case AppModes.StaticQuarto:
      return staticQuartoAppDescriptor(isSite(app));

    case AppModes.Static:
      return staticAppDescriptor(isSite(app), isPlot(app), isPin(app));

    case AppModes.Proxied:
      return "Proxied content";

    case AppModes.PlumberApi:
    case AppModes.PythonApi:
    case AppModes.PythonFastAPI:
      return "API";

    case AppModes.PythonDash:
      return "Dash application";

    case AppModes.PythonGradio:
      return "Gradio application";

    case AppModes.PythonStreamlit:
      return "Streamlit application";

    case AppModes.PythonBokeh:
      return "Bokeh application";

    case AppModes.PythonShiny:
      return "Python Shiny application";

    case AppModes.TensorFlowApi:
      return "TensorFlow Model API";

    case AppModes.StaticJupyter:
      return "Jupyter notebook";

    case AppModes.JupyterVoila:
      return "Voilà interactive notebook";

    default:
      return "Incomplete content";
  }
}

export function initialState() {
  return {
    isGitDeployment: false,
    app: {
      name: null,
      title: null,
      description: "",
    },
    /**
     * Contains the working copy of changes that can be committed.
     * Dirty should be computed based on the state of these.
     */
    working: {
      title: null,
      description: "",
      thumbnail: null,
      checkForUpdates: null,
    },
    thumbnailImage: null,
    thumbnailURL: "",
    error: null,
    canEdit: false,
    canEditThumbnail: false,
    usageData: [],
    usageError: null,
    canViewUsage: false,
    thumbnailError: null,
    gitError: null,
    apiType: null,
    accessCount: null,
    maxAccessCount: null,
    accessByDay: null,
    sessionTime: null,
    partial: null,
    chartData: {
      datasets: [],
      labels: [],
    },
    contentCreatedDateLabel: null,
  };
}

export default {
  state: initialState(),
  mutations: {
    [INFO_SETTINGS_RECEIVED](
      state,
      {
        app,
        canEdit,
        canEditThumbnail,
        contentCreatedDateLabel,
        owner,
        isGitDeployment,
        canViewUsage,
      },
    ) {
      state.app = {
        ...app,
        ...userObjectToOwnerProperties(owner),
        // Extremely narrow edge case. Only possible in bundle deploy.
        title: app.title || app.name,
        typeDescription: getAppTypeDescription(app),
      };
      state.canEdit = canEdit;
      state.canEditThumbnail = canEditThumbnail;
      state.contentCreatedDateLabel = contentCreatedDateLabel;
      state.isGitDeployment = isGitDeployment;
      state.canViewUsage = canViewUsage;
    },
    [INFO_SETTINGS_ERROR](state, error) {
      state.error = error;
    },
    [USAGE_DATA_RECEIVED](
      state,
      {
        usageData,
        accessCount,
        maxAccessCount,
        accessByDay,
        apiType,
        partial,
        sessionTime = null,
      },
    ) {
      state.apiType = apiType;
      state.accessCount = accessCount;
      state.maxAccessCount = maxAccessCount;
      state.accessByDay = accessByDay;
      state.sessionTime = sessionTime;
      state.partial = partial;
      state.usageData = usageData;
    },
    [USAGE_DATA_ERROR](state, error) {
      state.usageError = error;
    },
    [CHART_DATA_RECEIVED](state, chartData) {
      state.chartData = chartData;
    },
    [RESET_CHART_DATA](state) {
      state.chartData = {
        datasets: [],
        labels: [],
      };
    },
    [THUMBNAIL_URL_RECEIVED](state, thumbnailURL) {
      state.thumbnailURL = thumbnailURL;
    },
    [THUMBNAIL_RECEIVED](state, thumbnailImage) {
      state.thumbnailImage = thumbnailImage;
    },
    [INFO_SETTINGS_DISCARD](state) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async [INFO_SETTINGS_INIT]({ commit, dispatch, rootState }, guid) {
      const user = rootState.currentUser.user;
      const hiddenFromViewers =
        rootState.server.settings.viewersCanOnlySeeThemselves;
      // clear error, if any
      commit(INFO_SETTINGS_ERROR, null);
      // clear working copy
      commit(INFO_SETTINGS_DISCARD);
      try {
        const app = await getContent(guid);
        const owner =
          hiddenFromViewers && user.isViewer()
            ? ""
            : await getUser(app.ownerGuid);
        const canEdit = user.canEditAppSettings(app);
        const canEditThumbnail =
          canEdit && AppRoles.isCollaborator(app.appRole);
        const canViewUsage = user.isPublisher() || user.isAdmin();

        await dispatch(FETCH_GIT_INFO, { guid });

        const isGitDeployment = !isEmpty(rootState.contentView.git);
        const contentCreatedDateLabel = dayjs(app.createdTime)
          .subtract(1)
          .format(DAY_FORMAT);
        commit(INFO_SETTINGS_RECEIVED, {
          app,
          canEdit,
          canEditThumbnail,
          owner,
          contentCreatedDateLabel,
          isGitDeployment,
          canViewUsage,
        });
        if (canViewUsage) {
          dispatch(FETCH_USAGE);
        }
        dispatch(FETCH_THUMBNAIL);
      } catch (err) {
        commit(INFO_SETTINGS_ERROR, err);
      }
    },
    async [FETCH_USAGE]({ state, commit, dispatch }) {
      try {
        const apiType = getContentUsageAPIType(state.app.appMode);
        /* eslint-disable camelcase */
        const params = {
          content_guid: state.app.guid,
          from: dayjs().subtract(30, "days").format("YYYY-MM-DDTHH:mm:ss[Z]"),
          min_data_version: CONTENT_USAGE_API_VERSION,
          limit: MAX_CONTENT_USAGE_API_LIMIT,
          asc_order: false,
        };
        /* eslint-enable camelcase */
        let usageData = [],
          sessionTime = 0;

        switch (apiType) {
          case ContentUsageAPIType.Shiny:
            usageData = await drainPaging(
              getShinyUsage,
              params,
              CONTENT_USAGE_API_TOTAL_LIMIT,
            );

            sessionTime = usageData.reduce(
              (time, data) =>
                time.add(
                  dayjs(data.ended || undefined).diff(dayjs(data.started)),
                ),
              dayjs.duration(0),
            );
            break;
          case ContentUsageAPIType.General:
            usageData = await drainPaging(
              getContentVisits,
              params,
              CONTENT_USAGE_API_TOTAL_LIMIT,
            );
            break;
        }
        let accessCount = usageData.length;

        const accessByDay = usageData.reduce((map, data) => {
          const day = dayjs(data.time || data.started).format(DAY_FORMAT);
          map[day] = map[day] ? map[day] + 1 : 1;
          return map;
        }, {});

        const maxAccessCount = Object.values(accessByDay).reduce(
          (max, next) => Math.max(max, next),
          0,
        );
        const partial = accessCount >= CONTENT_USAGE_API_TOTAL_LIMIT;

        if (partial) {
          accessCount = `${CONTENT_USAGE_API_TOTAL_LIMIT / 1000}K+`;
          sessionTime = 0;
        }

        commit(USAGE_DATA_RECEIVED, {
          usageData,
          apiType,
          accessByDay,
          accessCount,
          maxAccessCount,
          sessionTime,
          partial,
        });
        dispatch(RESOLVE_CHART_DATA);
      } catch (err) {
        commit(USAGE_DATA_ERROR, err);
      }
    },
    [RESOLVE_CHART_DATA]({ state, commit }) {
      const { accessByDay } = state;
      const accessDays = Object.keys(accessByDay).map((day) =>
        dayjs(day, DAY_FORMAT, true),
      );
      const cutoff = dayjs.min(accessDays);
      const days = Array.from({ length: 30 }, (e, i) =>
        dayjs().subtract(29 - i, "days"),
      );
      // We have to do this kinda strangely. ChartJS ignores NaN, so NaN represents everything outside the current line.
      const beforeCutoff = days.map((day) => (day.isBefore(cutoff) ? 0 : NaN));
      const afterCutoff = days.map((day) =>
        day.isSameOrAfter(cutoff)
          ? accessByDay[day.format(DAY_FORMAT)] || 0
          : NaN,
      );
      commit(CHART_DATA_RECEIVED, {
        datasets: [
          {
            // Actual content usage line
            data: afterCutoff,
            backgroundColor: "#c7ddef",
            borderColor: "#337ab7",
            borderWidth: 1,
            fill: true,
            tension: 0, // - Disable Bezier Curves for harsher graph...
          },
          {
            // lead line before deployment
            data: beforeCutoff,
            backgroundColor: "#999999",
            borderColor: "#999999",
            borderWidth: 1,
            fill: false,
            tension: 0, // - Disable Bezier Curves for harsher graph...
            borderDash: [3, 3],
          },
        ],
        labels: days.map((day) => day.format(DAY_FORMAT)),
      });
    },
    async [FETCH_THUMBNAIL]({ state, commit }) {
      const { guid } = state.app;
      try {
        const thumbnailImageBlob = await getContentThumbnail(guid);
        if (thumbnailImageBlob.size === 0) {
          commit(THUMBNAIL_RECEIVED, null);
          commit(THUMBNAIL_URL_RECEIVED, "");
        } else {
          commit(THUMBNAIL_RECEIVED, thumbnailImageBlob);
          // MUST revoke the thumbnail URL!!!
          const thumbnailImageURL =
            window.URL.createObjectURL(thumbnailImageBlob);
          commit(THUMBNAIL_URL_RECEIVED, thumbnailImageURL);
        }
      } catch (e) {
        commit(INFO_SETTINGS_ERROR, e);
      }
    },
    async [SAVE_INFO_SETTINGS](
      { state, dispatch, commit },
      {
        title,
        description,
        thumbnail,
        repositoryEnabled: checkForUpdates,
        thumbnailCleared,
      },
    ) {
      try {
        await updateContent(state.app.guid, {
          title,
          description,
        });
        commit(UPDATE_APP_METADATA, { title, description });
        if (thumbnail) {
          await setContentThumbnail(state.app.guid, thumbnail);
        } else if (thumbnailCleared) {
          await deleteContentThumbnail(state.app.guid);
        }
        if (state.isGitDeployment) {
          await updateGitRepository(state.app.id, { enabled: checkForUpdates });
        }
        dispatch(INFO_SETTINGS_INIT, state.app.guid);
      } catch (e) {
        commit(INFO_SETTINGS_ERROR, e);
      }
    },
  },
};
